import { trackAnalyticsEvent } from '@autone/analytics';
import { useAutoneTranslation } from '@autone/translations';
import {
  Button,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from '@autone/ui-next/atoms';
import { usePaginatedSelectionContext } from '@autone/ui-next/components';
import { useCallback, useMemo, useState } from 'react';

import {
  managedByAutoneToBoolean,
  matchSystemRecommendationToBoolean,
} from '../../../../../utils/quick-filters';
import { type APP_NAME } from '../../../../consts';
import { useBulkEditAssortmentSkuLocationsMutation } from '../../../../redux/assortment';
import { useAssortmentSessionStorageAtom } from '../../../../storage/session-storage';
import { useGetSkuLocationsTableQueryArgs } from '../hooks/useGetSkuLocationsTableQueryArgs';
import { useGetSkuLocationsTableSelectionCounts } from '../hooks/useGetSkuLocationsTableSelectionCounts';
import { parseRowId } from '../utilities/row-id';

import { DoubleConfirmationInput } from './DoubleConfirmationInput';

interface SkuLocationsTableAutomateAssortmentModalContentProps {
  onEditComplete?: (result: 'ok' | 'error') => void;
}

export const SkuLocationsTableAutomateAssortmentModalContent = ({
  onEditComplete,
}: SkuLocationsTableAutomateAssortmentModalContentProps) => {
  const {
    body: { filters, advanced_filter },
  } = useGetSkuLocationsTableQueryArgs({ searchTerm: '' });
  const [{ matchSystemRecommendationToggle, managedByAutoneToggle }] =
    useAssortmentSessionStorageAtom('skuLocationsTable.extrasMenu');

  const { t, common } = useAutoneTranslation<typeof APP_NAME>();
  const [confirmationText, setConfirmationText] = useState<number | null>(null);
  const [state] = usePaginatedSelectionContext();
  const { selectedRowCount } = state;

  const [bulkEditSkuLocations, { isLoading }] =
    useBulkEditAssortmentSkuLocationsMutation();

  const { data, isFetching } = useGetSkuLocationsTableSelectionCounts();
  const { counts } = data ?? {};

  const confirmationOk = useMemo(
    () => confirmationText === selectedRowCount,
    [confirmationText, selectedRowCount],
  );

  const handleBulkEditSkuLocations = useCallback(async () => {
    const isMangedByAutone = managedByAutoneToBoolean(managedByAutoneToggle);
    const matchesSystemRecommendation = matchSystemRecommendationToBoolean(
      matchSystemRecommendationToggle,
    );

    const result = await (async () => {
      if (state.mode === 'bulk') {
        return await bulkEditSkuLocations({
          body: {
            selection: {
              selection_type: 'criteria',
              filters,
              advanced_filter,
              sku_locations_to_exclude: state.excludeList.map((id) => {
                const { skuId, locationId } = parseRowId(id);
                return { sku_id: skuId, location_id: locationId };
              }),
              is_managed_by_user:
                isMangedByAutone === null ? null : !isMangedByAutone,
              matches_system_recommendation: matchesSystemRecommendation,
            },
            assortment_update: {
              update_type: 'is_managed_by_user',
              new_value: false,
            },
          },
        });
      } else {
        return await bulkEditSkuLocations({
          body: {
            selection: {
              selection_type: 'sku_location_list',
              sku_locations: state.includeList.map((id) => {
                const { skuId, locationId } = parseRowId(id);
                return { sku_id: skuId, location_id: locationId };
              }),
            },
            assortment_update: {
              update_type: 'is_managed_by_user',
              new_value: false,
            },
          },
        });
      }
    })();

    if ('error' in result) {
      onEditComplete?.('error');
    } else {
      trackAnalyticsEvent({
        category: 'assortment_tables',
        action: 'sku_locations_table:bulk_automate_assortment_updated',
        properties: {
          num_sku_locations: state.selectedRowCount,
        },
      });
      onEditComplete?.('ok');
    }
  }, [
    bulkEditSkuLocations,
    onEditComplete,
    managedByAutoneToggle,
    matchSystemRecommendationToggle,
    filters,
    advanced_filter,
    state,
  ]);

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{t('bulk-actions.automate-assortment.title')}</DialogTitle>
      </DialogHeader>
      <DialogDescription>
        <p>{t('bulk-actions.automate-assortment.description')}</p>
        {isFetching ? (
          <Skeleton className="h-4 w-full mt-2" />
        ) : (
          <p className="mt-4 font-medium">
            {t('bulk-actions.automate-assortment.automating-x-sku-locs', {
              count: state.selectedRowCount,
              products: common.withValue('products', {
                value: counts?.products,
                count: counts?.products,
              }),
              locations: common.withValue('locations', {
                value: counts?.locations,
                count: counts?.locations,
              }),
            })}
          </p>
        )}
      </DialogDescription>
      <DoubleConfirmationInput
        id="automate-assortment-double-confirmation-input"
        selectedRowCount={selectedRowCount}
        value={confirmationText}
        onChange={(value) => setConfirmationText(value)}
      />
      <DialogFooter className="w-full flex flex-row justify-between">
        <DialogClose asChild>
          <Button
            id={`cancel-autome-assortment-button`}
            aria-label={common('buttons.cancel')}
            variant={'outline'}
          >
            {common('buttons.cancel')}
          </Button>
        </DialogClose>
        <Button
          loading={isLoading}
          id={`automate-assortment-button`}
          aria-label={t('action-bar.automate-assortment')}
          variant={'default'}
          disabled={!confirmationOk}
          onClick={handleBulkEditSkuLocations}
        >
          {t('action-bar.automate-assortment')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};
